import React, { useEffect, useRef, useState } from 'react';

import { useLocalStorage } from 'react-use';

import { useAppSelector, useBreakpoints } from '@pkg/hooks';
import WebGL from '@pkg/webgl';

import { useNavigationStore } from '@app/store/zustand';

import {
    AdminSection,
    LoggedOutSection,
    MainSection,
    MinimizedSection,
    PodsSection,
    ReportAnIssueAction,
    SettingsSection,
    WalletSection,
    WishlistsSection,
    YatFantasySection,
    YativerseSection,
} from './Sections';
import { SidebarFooter } from './SidebarFooter';
import { SidebarTop } from './SidebarTop';
import { sidebarWidthAnimation } from './animations';

import { Background, CloseButton, Expanded, Sidebar, SidebarBottom, SidebarMiddle, Wrapper } from './styles';

const SIDEBAR_FANTASY_STATE_KEY = 'nav-sidebar-fantasy';
const SIDEBAR_WISHLISTS_STATE_KEY = 'nav-sidebar-wishlists';
const SIDEBAR_PODS_STATE_KEY = 'nav-sidebar-pods';

interface SidebarNavProps {
    displayZIndexTop?: boolean;
}

export const SidebarNav = ({ displayZIndexTop }: SidebarNavProps) => {
    const { isMobile } = useBreakpoints();

    const isMinimized = useNavigationStore((state) => state.isMinimized);
    const toggleMobileMenu = useNavigationStore((state) => state.toggleMobileMenu);

    const [isFantasyOpen, setIsFantasyOpen] = useLocalStorage(SIDEBAR_FANTASY_STATE_KEY, false);
    const [isWishlistsOpen, setIsWishlistsOpen] = useLocalStorage(SIDEBAR_WISHLISTS_STATE_KEY, true);
    const [isPodsOpen, setIsPodsOpen] = useLocalStorage(SIDEBAR_PODS_STATE_KEY, true);
    const [isYativerseOpen, setIsYativerseOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);

    const [isAnimating, setIsAnimating] = useState(false);
    const [overflow, setOverflow] = useState('visible');

    useEffect(() => {
        if (isAnimating || isMinimized) {
            setOverflow('hidden');
        } else {
            setTimeout(() => {
                setOverflow('visible');
            }, 1000);
        }
    }, [isAnimating, isMinimized]);

    const scrollRef = useRef<HTMLDivElement>(null);

    const isAdmin = useAppSelector((state) => state.user.userClass?.isAdmin);
    const isRegisteredUser = useAppSelector((state) => state.user.isRegisteredUser);

    const desktopWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!desktopWrapperRef.current) return;
        WebGL.observeNavigation(desktopWrapperRef.current);

        return () => {
            WebGL.observeNavigation(false);
        };
    }, [desktopWrapperRef]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (isSettingsOpen && scrollRef) {
            timer = setTimeout(() => {
                scrollRef.current?.scrollTo({ top: scrollRef.current?.scrollHeight, behavior: 'smooth' });
            }, 800);
        }

        return () => clearTimeout(timer);
    }, [isSettingsOpen, scrollRef]);

    /* Mobile
    --------------------------- */

    if (isMobile) {
        return (
            <>
                <Wrapper>
                    <Sidebar key="sidebar-mobile">
                        <SidebarTop isAuthenticated={isRegisteredUser} />

                        <Expanded>
                            <SidebarMiddle>
                                {isRegisteredUser ? (
                                    <>
                                        <WalletSection />

                                        <YativerseSection
                                            isStatic={true}
                                            isOpen={true}
                                            setIsOpen={setIsYativerseOpen}
                                        />

                                        <SettingsSection isStatic={true} isOpen={true} setIsOpen={setIsSettingsOpen} />

                                        {isAdmin && (
                                            <AdminSection isStatic={true} isOpen={true} setIsOpen={setIsAdminOpen} />
                                        )}
                                    </>
                                ) : (
                                    <LoggedOutSection />
                                )}
                            </SidebarMiddle>
                        </Expanded>

                        <CloseButton onClick={() => toggleMobileMenu(false)}>Close</CloseButton>
                    </Sidebar>
                </Wrapper>
            </>
        );
    }

    /* Desktop
    --------------------------- */

    return (
        <>
            <Background
                $zIndexTop={displayZIndexTop}
                variants={sidebarWidthAnimation}
                initial={false}
                animate={isMinimized ? 'minimized' : 'expanded'}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
            />
            <Wrapper ref={desktopWrapperRef} $zIndexTop={displayZIndexTop}>
                <Sidebar
                    variants={sidebarWidthAnimation}
                    initial={false}
                    animate={isMinimized ? 'minimized' : 'expanded'}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    key="sidebar-desktop"
                    onAnimationStart={() => setIsAnimating(true)}
                    onAnimationComplete={() => setIsAnimating(false)}
                    style={{ overflow }}
                >
                    <SidebarTop isAuthenticated={isRegisteredUser} />

                    {!isMinimized && (
                        <Expanded ref={scrollRef}>
                            <MainSection isAuthenticated={isRegisteredUser} />

                            <SidebarMiddle>
                                <YatFantasySection isOpen={Boolean(isFantasyOpen)} setIsOpen={setIsFantasyOpen} />

                                {isRegisteredUser && (
                                    <WishlistsSection
                                        isOpen={Boolean(isWishlistsOpen)}
                                        setIsOpen={setIsWishlistsOpen}
                                    />
                                )}

                                <PodsSection
                                    isOpen={Boolean(isPodsOpen)}
                                    setIsOpen={setIsPodsOpen}
                                    isAuthenticated={isRegisteredUser}
                                />

                                <YativerseSection isOpen={Boolean(isYativerseOpen)} setIsOpen={setIsYativerseOpen} />
                            </SidebarMiddle>

                            <SidebarBottom>
                                {isRegisteredUser && (
                                    <>
                                        <SettingsSection
                                            isOpen={Boolean(isSettingsOpen)}
                                            setIsOpen={setIsSettingsOpen}
                                        />

                                        {isAdmin && <AdminSection isOpen={isAdminOpen} setIsOpen={setIsAdminOpen} />}
                                    </>
                                )}
                                <ReportAnIssueAction />
                            </SidebarBottom>
                        </Expanded>
                    )}

                    {isMinimized && <MinimizedSection isAuthenticated={isRegisteredUser} />}

                    <SidebarFooter />
                </Sidebar>
            </Wrapper>
        </>
    );
};
